import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Coins,
    Plus,
    Minus,
    Search,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Trash2,
    Receipt,
} from "lucide-react";
import api from "../../lib/api";
import { imgSrc } from "../../lib/imgSrc";

/**
 * BatchCashOrderPanel — Admin → Shop → Batch cash sale.
 *
 * Rapid-entry workflow for logging in-person cash sales (craft fairs,
 * bake sales, school events). No customer info tracked — the purpose
 * is to record WHICH products sold in WHAT quantities so stock and
 * the ledger reflect reality after an event.
 *
 * One submit → one order (paid=cash, delivered, pickup) → 100% of margin
 * flows into the General Support Fund via auto_post_order on the backend.
 */
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

import { CustomerAddressFields, emptyAddress, hasAddress } from "../CustomerAddressFields";

const BatchCashOrderPanel = () => {
    const [customerName, setCustomerName] = useState("");
    const [address, setAddress] = useState(emptyAddress);
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [search, setSearch] = useState("");
    const [qtyByProduct, setQtyByProduct] = useState({});
    const [eventLabel, setEventLabel] = useState("");
    const [notes, setNotes] = useState("");
    const [allowOversell, setAllowOversell] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lastReceipt, setLastReceipt] = useState(null);

    const loadProducts = async () => {
        setLoadingProducts(true);
        try {
            const r = await api.get("/admin/products");
            setProducts(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error("Failed to load products");
        } finally {
            setLoadingProducts(false);
        }
    };
    useEffect(() => { loadProducts(); }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return products
            .filter((p) => p && p.id)
            .filter((p) => {
                if (!q) return true;
                return (
                    (p.name || "").toLowerCase().includes(q) ||
                    (p.sku || "").toLowerCase().includes(q) ||
                    (p.category || "").toLowerCase().includes(q)
                );
            })
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [products, search]);

    const setQty = (pid, val) => {
        const n = Math.max(0, Math.floor(Number(val) || 0));
        setQtyByProduct((prev) => {
            const next = { ...prev };
            if (n <= 0) delete next[pid];
            else next[pid] = n;
            return next;
        });
    };

    const bump = (pid, delta) => {
        const cur = qtyByProduct[pid] || 0;
        setQty(pid, cur + delta);
    };

    const lineItems = useMemo(() => {
        return Object.entries(qtyByProduct)
            .map(([pid, qty]) => {
                const p = products.find((x) => x.id === pid);
                if (!p) return null;
                return { product: p, qty };
            })
            .filter(Boolean);
    }, [qtyByProduct, products]);

    const total = useMemo(
        () => lineItems.reduce((s, li) => s + Number(li.product.price || 0) * li.qty, 0),
        [lineItems]
    );
    const totalUnits = useMemo(
        () => lineItems.reduce((s, li) => s + li.qty, 0),
        [lineItems]
    );

    const stockIssues = useMemo(
        () => lineItems.filter((li) => Number(li.product.stock || 0) < li.qty),
        [lineItems]
    );

    const clearAll = () => {
        setCustomerName(""); setAddress(emptyAddress);
        setQtyByProduct({});
        setEventLabel("");
        setNotes("");
        setLastReceipt(null);
    };

    const submit = async () => {
        if (!customerName.trim() || !hasAddress(address)) { toast.error("Customer name and address are required for every cash order."); return; }
        if (lineItems.length === 0) {
            toast.error("Add at least one item before submitting.");
            return;
        }
        if (stockIssues.length > 0 && !allowOversell) {
            toast.error("Some items exceed available stock. Toggle 'Allow oversell' or reduce quantities.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/admin/orders/batch-cash", {
                ...address, customer_name: customerName.trim(),
                event_label: eventLabel.trim() || null,
                notes: notes.trim() || "",
                allow_oversell: allowOversell,
                items: lineItems.map((li) => ({ product_id: li.product.id, quantity: li.qty })),
            });
            const data = r.data || {};
            setLastReceipt({
                order_number: data.order_number,
                total: data.total,
                line_count: data.line_count,
                lines: lineItems.map((li) => ({
                    name: li.product.name,
                    qty: li.qty,
                    price: li.product.price,
                    subtotal: Number(li.product.price || 0) * li.qty,
                })),
                event_label: eventLabel.trim() || "Cash sale — in-person event",
                created_at: new Date().toISOString(),
            });
            toast.success(`Order ${data.order_number} logged · ${currency(data.total)}`);
            setCustomerName(""); setAddress(emptyAddress);
            setQtyByProduct({});
            setEventLabel("");
            setNotes("");
            await loadProducts();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Check the customer address and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="batch-cash-order-panel">
            {/* Header */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                        <Coins className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">Customer cash sale</h2>
                        <p className="text-sm text-[#4A5568] mt-1">
                            In-person pickup · Cash payment · General Support Fund
                        </p>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">
                            Event label <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={eventLabel}
                            onChange={(e) => setEventLabel(e.target.value)}
                            placeholder="e.g. Fall Craft Fair 2026"
                            data-testid="batch-cash-event-label"
                            className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">
                            Notes <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Booth 12, Saturday session"
                            data-testid="batch-cash-notes"
                            className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-3"><h3 className="font-heading text-lg" data-testid="batch-cash-customer-heading">Customer details</h3><label className="text-xs text-[#4A5568] block">Customer name *<input data-testid="batch-cash-customer-name" required value={customerName} onChange={e => setCustomerName(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-black/15 w-full text-sm" /></label><CustomerAddressFields value={address} onChange={setAddress} prefix="batch-cash" /></div>
            {/* Product picker + running total */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                {/* Product grid */}
                <div className="bg-white border border-black/10 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, SKU, or category"
                                data-testid="batch-cash-search"
                                className="w-full pl-9 pr-3 py-2 bg-[#F4F1EA] border border-transparent rounded-lg text-sm focus:outline-none focus:bg-white focus:border-[#E53935]"
                            />
                        </div>
                        <span className="text-xs text-[#4A5568] whitespace-nowrap">{filtered.length} product{filtered.length === 1 ? "" : "s"}</span>
                    </div>

                    {loadingProducts ? (
                        <div className="py-16 text-center text-[#4A5568] flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 text-center text-[#4A5568] text-sm">No products match your search.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[calc(100vh-24rem)] overflow-y-auto pr-1">
                            {filtered.map((p) => {
                                const qty = qtyByProduct[p.id] || 0;
                                const active = qty > 0;
                                const stock = Number(p.stock || 0);
                                const over = qty > stock;
                                const firstImg = Array.isArray(p.images) && p.images.length > 0
                                    ? (typeof p.images[0] === "string" ? p.images[0] : (p.images[0]?.url || ""))
                                    : (p.image_url || "");
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`batch-cash-product-${p.id}`}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                            active
                                                ? over
                                                    ? "border-amber-400 bg-amber-50"
                                                    : "border-[#E53935]/40 bg-[#E53935]/5"
                                                : "border-black/10 bg-white hover:border-black/25"
                                        }`}
                                    >
                                        <div className="w-12 h-12 rounded-lg bg-[#F4F1EA] overflow-hidden flex-shrink-0">
                                            {firstImg ? (
                                                <img src={imgSrc(firstImg, 96, 70)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                            ) : null}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-[#1C252E] truncate">{p.name}</p>
                                            <p className="text-xs text-[#4A5568]">
                                                {currency(p.price)} · stock {stock}
                                                {p.sku ? <span className="text-[#4A5568]/70"> · {p.sku}</span> : null}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => bump(p.id, -1)}
                                                disabled={qty === 0}
                                                data-testid={`batch-cash-dec-${p.id}`}
                                                className="w-8 h-8 rounded-lg border border-black/10 flex items-center justify-center text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed"
                                                aria-label={`Decrease ${p.name}`}
                                            >
                                                <Minus className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                            <input
                                                type="number"
                                                min="0"
                                                value={qty || ""}
                                                onChange={(e) => setQty(p.id, e.target.value)}
                                                placeholder="0"
                                                data-testid={`batch-cash-qty-${p.id}`}
                                                className="w-14 h-8 text-center text-sm border border-black/15 rounded-lg focus:outline-none focus:border-[#E53935]"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => bump(p.id, 1)}
                                                data-testid={`batch-cash-inc-${p.id}`}
                                                className="w-8 h-8 rounded-lg border border-black/10 flex items-center justify-center text-[#4A5568] hover:bg-[#F4F1EA]"
                                                aria-label={`Increase ${p.name}`}
                                            >
                                                <Plus className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Running total / summary */}
                <div className="bg-white border border-black/10 rounded-2xl p-5 h-fit lg:sticky lg:top-24">
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium mb-3">This batch</h3>
                    {lineItems.length === 0 ? (
                        <p className="text-sm text-[#4A5568]">No items selected yet. Tap + on any product to start.</p>
                    ) : (
                        <>
                            <ul className="divide-y divide-black/5 max-h-72 overflow-y-auto -mx-2 px-2" data-testid="batch-cash-summary-list">
                                {lineItems.map((li) => (
                                    <li key={li.product.id} className="py-2 flex items-center gap-2 text-sm">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-[#1C252E] truncate">{li.product.name}</p>
                                            <p className="text-xs text-[#4A5568]">
                                                {li.qty} × {currency(li.product.price)}
                                                {Number(li.product.stock || 0) < li.qty ? (
                                                    <span className="ml-2 text-amber-700 font-medium">
                                                        · over stock ({Number(li.product.stock || 0)})
                                                    </span>
                                                ) : null}
                                            </p>
                                        </div>
                                        <span className="font-medium text-[#1C252E] whitespace-nowrap">
                                            {currency(Number(li.product.price || 0) * li.qty)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setQty(li.product.id, 0)}
                                            data-testid={`batch-cash-remove-${li.product.id}`}
                                            className="text-[#4A5568] hover:text-[#E53935] p-1"
                                            aria-label={`Remove ${li.product.name}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-black/10 mt-3 pt-3 flex justify-between text-sm">
                                <span className="text-[#4A5568]">{totalUnits} unit{totalUnits === 1 ? "" : "s"} · {lineItems.length} line{lineItems.length === 1 ? "" : "s"}</span>
                                <span className="font-heading text-xl text-[#1C252E]" data-testid="batch-cash-total">{currency(total)}</span>
                            </div>
                        </>
                    )}

                    {stockIssues.length > 0 && (
                        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <div>
                                {stockIssues.length} line{stockIssues.length === 1 ? "" : "s"} exceed{stockIssues.length === 1 ? "s" : ""} available stock.
                                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={allowOversell}
                                        onChange={(e) => setAllowOversell(e.target.checked)}
                                        data-testid="batch-cash-allow-oversell"
                                        className="w-4 h-4 accent-[#E53935]"
                                    />
                                    <span className="font-medium">Allow oversell (stock goes negative)</span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex gap-2">
                        <button
                            type="button"
                            onClick={clearAll}
                            disabled={submitting || (lineItems.length === 0 && !eventLabel && !notes)}
                            data-testid="batch-cash-clear"
                            className="px-4 py-2.5 text-sm rounded-lg border border-black/15 text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting || lineItems.length === 0 || (stockIssues.length > 0 && !allowOversell)}
                            data-testid="batch-cash-submit"
                            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-[#E53935] text-white hover:bg-[#c62828] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Logging…</>
                            ) : (
                                <><CheckCircle2 className="w-4 h-4" /> Log batch ({currency(total)})</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Last receipt */}
            {lastReceipt && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="batch-cash-last-receipt">
                    <div className="flex items-start gap-3">
                        <Receipt className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-emerald-900">
                                Logged <span className="font-medium">{lastReceipt.order_number}</span> · {lastReceipt.event_label}
                            </p>
                            <p className="text-xs text-emerald-800 mt-0.5">
                                {lastReceipt.line_count} line{lastReceipt.line_count === 1 ? "" : "s"} · {currency(lastReceipt.total)} · margin routed to GSF
                            </p>
                            <ul className="mt-2 text-xs text-emerald-900 divide-y divide-emerald-200/60">
                                {lastReceipt.lines.map((l, i) => (
                                    <li key={i} className="py-1 flex justify-between gap-3">
                                        <span className="truncate">{l.qty} × {l.name}</span>
                                        <span className="font-medium whitespace-nowrap">{currency(l.subtotal)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BatchCashOrderPanel;
